/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaMinus } from 'react-icons/fa';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import ReactMarkdown from 'react-markdown';
import './FloatingChat.css';
import 'react-resizable/css/styles.css';
import Tooltip from '@mui/material/Tooltip';
import { useLocation } from 'react-router-dom';
import { ChatProps } from '../services/chatApi';
import CircularProgress from '@mui/material/CircularProgress';
import JunkOutlineIcon from './JunkOutlineIcon';
import { AnchorHTMLAttributes } from 'react';

interface Size {
  width: number;
  height: number;
}

const MIN_WIDTH = 300;
const MIN_HEIGHT = 400;

const LinkRenderer = ({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
        >
            {children}
        </a>
    );
};

const FloatingChat: React.FC<ChatProps> = ({ inputValue, deleteChatHandler, submitHandler, messages, setInputHandler, isLoading }) => {
  const location = useLocation();
  const [isMinimized, setIsMinimized] = useState(() => {
    const saved = localStorage.getItem('chatMinimized');
    return saved ? JSON.parse(saved) : true;
  });

  const [isHidden, setIsHidden] = useState(() => {
    const saved = localStorage.getItem('chatHidden');
    return saved ? JSON.parse(saved) : true;
  });

  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('chatPosition');
    return saved ? JSON.parse(saved) : { x: 0, y: 0 };
  });

  const [size, setSize] = useState<Size>(() => {
    const saved = localStorage.getItem('chatSize');
    return saved ? JSON.parse(saved) : { width: MIN_WIDTH, height: MIN_HEIGHT };
  });

    useEffect(() => {
        const checkVisibility = async () => {
            if (location.pathname === '/assistant') {
                handleHide();
            }
            else {
                handleShow();
            }
        };
        checkVisibility();
    }, [location]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const nodeRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
    const inputRef = useRef<HTMLInputElement>(null);

  // Save state to localStorage
  useEffect(() => {
      localStorage.setItem('chatMinimized', JSON.stringify(isMinimized));
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isMinimized]);

  useEffect(() => {
      localStorage.setItem('chatHidden', JSON.stringify(isHidden));
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isHidden]);

  useEffect(() => {
    localStorage.setItem('chatPosition', JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    localStorage.setItem('chatSize', JSON.stringify(size));
  }, [size]);

  const handleMinimize = () => {
    setIsMinimized(true);
    };

    const handleHide = () => {
        setIsHidden(true);
    };

    const handleShow = () => {
        setIsHidden(false);
    };

  const handleMaximize = () => {
    setIsMinimized(false);
  };

  const handleDragStop = (_e: any, data: { x: number; y: number }) => {
    setPosition({ x: data.x, y: data.y });
  };

  const handleResize = (_e: any, { size }: { size: Size }) => {
    const newWidth = Math.max(size.width, MIN_WIDTH);
    const newHeight = Math.max(size.height, MIN_HEIGHT);
    
    // Calculate the new position to maintain the right edge
    const rightEdge = position.x + size.width;
    const newPosition = {
      x: rightEdge - newWidth,
      y: position.y
    };
    
    setSize({ width: newWidth, height: newHeight });
    setPosition(newPosition);
    };

    const handleSubmitAndFocus = async (e: React.FormEvent<HTMLFormElement>) => {
        await submitHandler(e);
        setTimeout(function () {
            inputRef.current.focus();
        }, 100);
    };

  if (isHidden) {
    return ("");
  }

  if (isMinimized) {
    return (
      <div className="floating-chat-minimized" onClick={handleMaximize}>
        <FaComments size={24} />
        {messages.length > 0 && <div className="message-indicator">{messages.length}</div>}
      </div>
    );
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={handleDragStop}
      bounds="parent"
      handle=".chat-header"
    >
      <div ref={nodeRef} className="floating-chat-container" style={{ width: size.width, height: size.height }}>
        <Resizable
          width={size.width}
          height={size.height}
          onResize={handleResize}
          minConstraints={[MIN_WIDTH, MIN_HEIGHT]}
          maxConstraints={[1400, 1000]}
          resizeHandles={['nw']}
        >
          <div style={{ width: size.width, height: size.height, display: 'flex', flexDirection: 'column' }}>
            <div className="chat-header">
              <span>PwC Migration Assistant</span>
                <div className="chat-controls">
                    <button onClick={handleMinimize} className="minimize-button">
                        <Tooltip title="Minimize">
                            <FaMinus size={12} />
                        </Tooltip>
                    </button>
                    {isLoading ? '' :
                        <button onClick={deleteChatHandler} className="delete-chat-button">
                            <Tooltip title="Delete chat">
                                <JunkOutlineIcon width={16} height={16} />
                            </Tooltip>
                        </button>
                    }
                </div>
            </div>
                <div className="chat-messages">
                    {messages.map(message => (
                        <div key={message.id} className={`message ${message.isUser ? 'user-message' : 'assistant-message'}`}>
                            <div className="message-content">
                                <ReactMarkdown
                                    components={{
                                        a: LinkRenderer
                                    }}
                                >
                                    {message.text}
                                </ReactMarkdown>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            <form onSubmit={handleSubmitAndFocus} className="chat-input-container">
            <input
                ref={inputRef}
                type="text"
                disabled={isLoading}
                value={inputValue}
                onChange={setInputHandler}
                placeholder="Type your message..."
                className="chat-input"
              />
            {isLoading ? <CircularProgress size="30px" /> : <button type="submit" className="send-button">
                Send
            </button> }
            </form>
          </div>
        </Resizable>
      </div>
    </Draggable>
  );
};

export default FloatingChat;